import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { smokeTestHistoryClient } from '@terra/shared-rest-clients-smoke-test-history';
import type { SmokeTestHistoryResponseDto } from '@terra/shared-contracts-smoke-test-history';
import { useAppTargetId, useTargetType } from '../../store/projectStore';
import { isAppTarget } from '../../utils/target-scope.utils';
import { getAuthHeaders } from '../../utils/authHeaders';

type UseSmokeTestHistoryProps = {
  threadId: string;
  enabled?: boolean;
};

export const SMOKE_TEST_HISTORY_QUERY_KEY = 'smoke-test-history';

export const useInvalidateSmokeTestHistory = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async ({
      applicationId,
      threadId,
    }: {
      applicationId?: string | null;
      threadId?: string | null;
    }) => {
      const queryKey =
        applicationId && threadId
          ? [SMOKE_TEST_HISTORY_QUERY_KEY, applicationId, threadId]
          : [SMOKE_TEST_HISTORY_QUERY_KEY];

      await queryClient.invalidateQueries({
        queryKey,
        exact: false,
      });
    },
    [queryClient],
  );
};

export function useSmokeTestHistory({
  threadId,
  enabled = true,
}: UseSmokeTestHistoryProps) {
  const appTargetId = useAppTargetId();
  const targetType = useTargetType();
  const queryClient = useQueryClient();
  const needsAppTargetId = targetType != null ? isAppTarget(targetType) : true;
  const isWaitingForAppTargetId =
    enabled && Boolean(threadId) && needsAppTargetId && !appTargetId;
  const invalidateHistory = useInvalidateSmokeTestHistory();
  const queryKey = [SMOKE_TEST_HISTORY_QUERY_KEY, appTargetId, threadId];

  const result = useQuery({
    queryKey,
    queryFn: async () => {
      if (!threadId) {
        throw new Error('Thread ID is required');
      }

      if (!appTargetId) {
        throw new Error('Application target is required');
      }

      const authHeaders = await getAuthHeaders();

      const response =
        await smokeTestHistoryClient.getSmokeTestHistoryByThreadId({
          params: { applicationId: appTargetId, threadId },
          extraHeaders: authHeaders,
        });

      if (response.status !== 200) {
        throw new Error('Failed to fetch smoke test history');
      }

      return response.body as SmokeTestHistoryResponseDto;
    },
    enabled: enabled && !!threadId && !!appTargetId,
    staleTime: 30000,
  });

  const setStepVisibilityMutation = useMutation({
    mutationFn: async ({
      stepKey,
      visible,
    }: {
      stepKey: string;
      visible: boolean;
    }) => {
      if (!threadId || !appTargetId) {
        throw new Error('Application target and thread id are required');
      }

      const authHeaders = await getAuthHeaders();
      const response = await smokeTestHistoryClient.setStoryTraceStepVisibility(
        {
          params: {
            applicationId: appTargetId,
            threadId,
            stepKey,
          },
          body: { visible },
          extraHeaders: authHeaders,
        },
      );

      if (response.status !== 200) {
        throw new Error('Failed to update story step visibility');
      }

      return response.body as SmokeTestHistoryResponseDto;
    },
    onMutate: async ({ stepKey, visible }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<SmokeTestHistoryResponseDto>(queryKey);

      if (previous?.steps) {
        queryClient.setQueryData<SmokeTestHistoryResponseDto>(queryKey, {
          ...previous,
          steps: previous.steps.map((step) =>
            step.stepKey === stepKey ? { ...step, visible } : step,
          ),
        });
      }

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: async () => {
      await invalidateHistory({ applicationId: appTargetId, threadId });
    },
  });

  const invalidate = useCallback(() => {
    return invalidateHistory({ applicationId: appTargetId, threadId });
  }, [appTargetId, invalidateHistory, threadId]);

  const setStepVisibility = useCallback(
    (input: { stepKey: string; visible: boolean }) => {
      return setStepVisibilityMutation.mutateAsync(input);
    },
    [setStepVisibilityMutation],
  );

  const isLoading = result.isLoading || isWaitingForAppTargetId;

  return {
    ...result,
    isLoading,
    invalidate,
    setStepVisibility,
  };
}
